import { useQueueRealtime } from "./useQueueRealtime";

/**
 * Single mount point for queue-related realtime cache reconciliation.
 *
 * The bridge calls `useQueueRealtime` once for the entire authenticated
 * app surface and renders nothing. Mounting here — inside
 * `<RealtimeProvider>` and `<QueryClientProvider>`, outside any route —
 * means every page that consumes `useQueueWorkItemTallies` (sidebar
 * badges in `WorkflowLayout`, the admin tree in `QueueAdminPage`, and
 * any future tally renderer) inherits live updates without remembering
 * to mount the hook locally. That latent "forgot to subscribe" failure
 * mode silently degrades badges to 15s-stale; centralizing the mount
 * eliminates the obligation.
 *
 * Invariant: render this component exactly once. The hook attaches
 * SignalR `WorkItemAdded` / `WorkItemCompleted` handlers per render
 * cycle, so a duplicate mount would invalidate the same caches twice
 * per event — harmless but wasteful.
 *
 * The bridge is named for its role (a transport-to-cache reconciler)
 * rather than the hook it wraps so that, as more app-wide realtime
 * subscriptions arrive (presence, in-app notifications, …), each
 * lands as a sibling bridge instead of accreting onto a generic
 * "subscriptions" grab-bag.
 */
export function QueueRealtimeBridge(): null {
    useQueueRealtime();
    return null;
}
